"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
function PostsCarouselConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="w-full">
        <div className="flex justify-between items-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold font-default text-foreground">
            Intelligence Hub
          </h2>
          <div className="flex gap-2">
            <div className="size-10 rounded-full bg-muted animate-pulse" />
            <div className="size-10 rounded-full bg-muted animate-pulse" />
          </div>
        </div>
        <div className="flex gap-6 overflow-hidden">
          {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="min-w-0 shrink-0 grow-0 basis-full md:basis-1/2 lg:basis-1/3">
              <Card className="border-none shadow-sm overflow-hidden">
                <AspectRatio ratio={16 / 9} className="bg-muted animate-pulse" />
                <CardContent className="p-6">
                  <div className="h-3 w-24 bg-muted animate-pulse mb-3 rounded" />
                  <div className="h-5 w-full bg-muted animate-pulse mb-2 rounded" />
                  <div className="h-5 w-2/3 bg-muted animate-pulse mb-4 rounded" />
                  <div className="space-y-2 mb-6">
                    <div className="h-3 w-full bg-muted animate-pulse rounded" />
                    <div className="h-3 w-full bg-muted animate-pulse rounded" />
                    <div className="h-3 w-4/5 bg-muted animate-pulse rounded" />
                  </div>
                  <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>;
  }
  if (error) {
    return <div className="text-center py-12 text-muted-foreground">
        <p>Failed to load latest intelligence reports.</p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return null;
  }
  return <Carousel opts={{
    align: "start",
    loop: false
  }} className="w-full">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end mb-10 gap-4">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold font-default text-foreground">
            Intelligence Hub
          </h2>
          <p className="text-muted-foreground mt-2 font-default">
            Stay updated with the latest software innovations and technical breakthroughs.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <CarouselPrevious className="static translate-y-0 translate-x-0 top-auto left-auto right-auto bottom-auto size-10 border-border hover:bg-accent hover:text-accent-foreground rounded-md transition-colors" />
          <CarouselNext className="static translate-y-0 translate-x-0 top-auto left-auto right-auto bottom-auto size-10 border-border hover:bg-accent hover:text-accent-foreground rounded-md transition-colors" />
        </div>
      </div>

      <CarouselContent className="-ml-6">
        {posts.map((post, i) => {
        const cleanExcerpt = post.excerpt ? post.excerpt.replace(/<[^>]*>?/gm, "") : "";
        const formattedDate = post.date ? new Date(post.date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric"
        }) : "Recent";
        return <CarouselItem key={post.id} data-index={i} className="pl-6 md:basis-1/2 lg:basis-1/3">
              <Card className="overflow-hidden border border-border shadow-md bg-card h-full flex flex-col transition-all duration-400 hover:shadow-lg rounded-lg">
                <Link to={post.link || "#"} className="relative block shrink-0 overflow-hidden">
                  <AspectRatio ratio={16 / 9}>
                    <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/software_insight_cover.png"} alt={post.title} className="object-cover w-full h-full transition-transform duration-500 hover:scale-105" />
                  </AspectRatio>
                  <div className="absolute top-4 left-4 bg-primary text-primary-foreground px-3 py-1 text-[12px] font-bold rounded-sm font-default">
                    {formattedDate}
                  </div>
                </Link>
                <CardContent className="p-6 flex flex-col flex-grow">
                  <div className="text-[14px] text-primary mb-2 font-bold tracking-wider uppercase font-default">
                    Innovation
                  </div>
                  <Link to={post.link || "#"} className="group block mb-3">
                    <h3 className="text-[20px] font-bold font-default text-foreground line-clamp-2 group-hover:text-primary transition-colors">
                      {post.title}
                    </h3>
                  </Link>
                  <p className="text-[16px] text-muted-foreground line-clamp-3 mb-6 flex-grow font-default">
                    {cleanExcerpt}
                  </p>
                  <Link to={post.link || "#"} className="inline-flex items-center text-primary font-bold text-sm group mt-auto w-fit transition-colors">
                    Learn More
                    <ArrowRight className="w-4 h-4 ml-1 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </CardContent>
              </Card>
            </CarouselItem>;
      })}
      </CarouselContent>
    </Carousel>;
}
export default function InnovationInsights() {
  const wp_query_params = {
    post_type: "post",
    per_page: 6,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="222810" id="intelligence-hub" className="bg-background text-foreground py-24 light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6,
        order: "desc",
        orderby: "date"
      }}>
          <PostsCarouselConsumer />
        </PostsProvider>
      </div>
    </section>;
}